/**
 * Org-wallet API client.
 *
 * Maintenance (super-admin): api/maintenance/org-wallet/...
 * Org-facing (tenant-isolated): api/analytics/tenants/{tenantId}/org-wallet/...
 *
 * The org wallet reimburses an org's users for their charging sessions. Balance
 * mutation happens in backend-stream-processor-go; these endpoints record intent
 * (settlement runs, deposits), edit config, and read state. All money in INR.
 */
import axiosInstance from "../axiosInstance";

export type SettlementMode = "per_transaction" | "monthly";

export interface OrgWallet {
  org_id: number;
  org_name: string | null;
  org_wallet_id: number | null;
  org_wallet_guid: string | null;
  current_balance: number;
  outstanding: number;
  available_balance: number;
  enabled: boolean;
  settlement_mode: SettlementMode;
  min_balance: number;
  settle_from: string | null;
}

export interface OrgWalletSettingsUpdate {
  enabled?: boolean;
  settlement_mode?: SettlementMode;
  min_balance?: number;
  settle_from?: string;
}

export interface OrgOption {
  org_id: number;
  org_name: string | null;
}

export interface SettlementRun {
  id: number;
  run_guid: string;
  org_id: number;
  scope_type: string;
  period_key: string | null;
  period_start: string;
  period_end: string;
  status: string;
  total_amount: number;
  session_count: number;
  user_count: number;
  triggered_by: string | null;
  settled_at: string | null;
  created_at: string;
}

export interface SettlementRunCreate {
  scope_type: "monthly" | "manual_range";
  period_key?: string;
  period_start?: string;
  period_end?: string;
}

export interface LedgerRow {
  id: number;
  org_wallet_transaction_guid: string;
  transaction_type: string | null;
  transaction_amount: number;
  wallet_balance_before: number;
  wallet_balance_after: number;
  transaction_state: string;
  created_date: string;
}

export interface ClaimRow {
  id: number;
  claim_guid: string;
  user_wallet_id: number;
  user_name: string | null;
  claim_amount: number;
  claim_state: string;
  settlement_run_id: number | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
}

export interface Recharge {
  id: number;
  recharge_guid: string;
  org_id: number;
  amount: number;
  method: string;
  reference_number: string | null;
  proof_url: string | null;
  notes: string | null;
  status: string;
  recorded_by: string;
  approved_by: string | null;
  reject_reason: string | null;
  created_at: string;
  approved_at: string | null;
  credited_at: string | null;
}

export interface AuditEntry {
  id: number;
  actor: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  detail: unknown;
  created_at: string;
}

export interface RangeSummaryRow {
  user_wallet_id: number;
  user_id: number;
  user_name: string | null;
  phone: string | null;
  session_count: number;
  total_amount: number;
  settled_count: number;
  unsettled_count: number;
  unsettled_amount: number;
}

export interface RangeSessionRow {
  session_debit_txn_id: number;
  user_wallet_id: number;
  user_name: string | null;
  session_guid: string | null;
  station_name: string | null;
  amount: number;
  created_date: string;
  settled: boolean;
}

// ---- maintenance (super-admin) ---------------------------------------------
const M = "api/maintenance/org-wallet";

export const listOrgWallets = () =>
  axiosInstance.get<OrgWallet[]>(`${M}/org-wallets`).then((r) => r.data);

// Orgs without a wallet yet — the picker for onboarding a new org.
export const candidateOrgs = () =>
  axiosInstance.get<OrgOption[]>(`${M}/candidate-orgs`).then((r) => r.data);

export const getOrgWallet = (orgId: number) =>
  axiosInstance.get<OrgWallet>(`${M}/org-wallets/${orgId}`).then((r) => r.data);

export const updateOrgWalletSettings = (orgId: number, body: OrgWalletSettingsUpdate) =>
  axiosInstance.put<OrgWallet>(`${M}/org-wallets/${orgId}/settings`, body).then((r) => r.data);

export const createSettlement = (orgId: number, body: SettlementRunCreate) =>
  axiosInstance.post<SettlementRun>(`${M}/org-wallets/${orgId}/settlements`, body).then((r) => r.data);

export const listSettlements = (orgId: number) =>
  axiosInstance.get<SettlementRun[]>(`${M}/org-wallets/${orgId}/settlements`).then((r) => r.data);

export const listLedger = (orgId: number) =>
  axiosInstance.get<LedgerRow[]>(`${M}/org-wallets/${orgId}/ledger`).then((r) => r.data);

export const listClaims = (orgId: number) =>
  axiosInstance.get<ClaimRow[]>(`${M}/org-wallets/${orgId}/claims`).then((r) => r.data);

export const recordRecharge = (orgId: number, form: FormData) =>
  axiosInstance
    .post<Recharge>(`${M}/org-wallets/${orgId}/recharges`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);

export const listRecharges = (orgId: number) =>
  axiosInstance.get<Recharge[]>(`${M}/org-wallets/${orgId}/recharges`).then((r) => r.data);

export const approveRecharge = (rechargeId: number) =>
  axiosInstance.post<Recharge>(`${M}/recharges/${rechargeId}/approve`).then((r) => r.data);

export const rejectRecharge = (rechargeId: number, reason?: string) =>
  axiosInstance.post<Recharge>(`${M}/recharges/${rechargeId}/reject`, { reason }).then((r) => r.data);

export const listAudit = (orgId: number) =>
  axiosInstance.get<AuditEntry[]>(`${M}/org-wallets/${orgId}/audit`).then((r) => r.data);

// Range report. from/to are UTC ISO (the caller converts the user's local days to UTC).
export const rangeSummary = (orgId: number, fromIso: string, toIso: string) =>
  axiosInstance
    .get<RangeSummaryRow[]>(`${M}/org-wallets/${orgId}/range-summary`, { params: { from_ts: fromIso, to_ts: toIso } })
    .then((r) => r.data);

export const rangeSessions = (orgId: number, fromIso: string, toIso: string, userWalletId?: number) =>
  axiosInstance
    .get<RangeSessionRow[]>(`${M}/org-wallets/${orgId}/range-sessions`, {
      params: { from_ts: fromIso, to_ts: toIso, user_wallet_id: userWalletId },
    })
    .then((r) => r.data);

// ---- org-facing (tenant) ----------------------------------------------------
const orgBase = (tenantId: string) => `api/analytics/tenants/${tenantId}/org-wallet`;

export interface OrgWalletBalance {
  org_wallet_guid: string | null;
  current_balance: number;
  outstanding: number;
  available_balance: number;
  enabled: boolean;
  settlement_mode: string;
}

export const getOrgWalletBalance = (tenantId: string) =>
  axiosInstance.get<OrgWalletBalance>(`${orgBase(tenantId)}/balance`).then((r) => r.data);

export const getOrgWalletLedger = (tenantId: string) =>
  axiosInstance.get<LedgerRow[]>(`${orgBase(tenantId)}/ledger`).then((r) => r.data);

export const getOrgWalletClaims = (tenantId: string) =>
  axiosInstance.get<ClaimRow[]>(`${orgBase(tenantId)}/claims`).then((r) => r.data);

export const getOrgWalletRecharges = (tenantId: string) =>
  axiosInstance.get<Recharge[]>(`${orgBase(tenantId)}/recharges`).then((r) => r.data);
